<template>
    <div>
        <h1 class="font-[700] text-3xl text-center text-title mb-[3vh]">
            {{ $t('pages.signup.step2.checkEmail') }}
        </h1>
        <div class="bg-card rounded-lg px-10 pt-14 pb-10 mb-[3vh] w-fit mx-auto">
            <p class="text-title text-center text-xl font-bold mb-[2vh] text-nowrap">
                {{ $t('pages.signup.step2.enterCode') }} {{ props.email }}
            </p>
            <!-- PinInput -->
            <div class="flex justify-center mb-4">
                <UPinInput 
                    v-model="codeArr"
                    :length="6"
                    type="number"
                    otp
                    :disabled="isLoading"
                    size="xl"
                    color="primary"
                    variant="outline"
                    class="gap-3"
                    @complete="validateCode"
                />
            </div>
            <div class="h-4">
                <p v-if="errorMessage" class="text-error text-center mb-2 text-sm">{{ errorMessage }}</p>
                <p v-if="isLoading" class="text-body text-center mb-2 text-sm">{{ $t('pages.signup.step2.validating') }}</p>
            </div>
        </div>

        <p class="text-center text-base font-bold text-body">
            {{ $t('pages.signup.step2.noCode') }}
            <span v-if="countDown === 0" class="text-interactive hover:underline font-bold cursor-pointer ml-1"
                @click="resendCode">
                {{ $t('pages.signup.step2.resend') }}
            </span>
            <span v-else>
                {{ $t('pages.signup.step2.resendIn') }}
                {{ countDown }}
                s
            </span>
        </p>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { sendRegisterCodeApi, validateCodeApi } from '~/api/auth';
import type { ApiErrorWithStatus } from '~/types/api';

const { t } = useI18n();
const emit = defineEmits(['toStep3']);

const props = defineProps<{ email: string }>();

const errorMessage = ref('');
const isLoading = ref(false);
const codeArr = ref<number[]>([]);

// 監聽驗證碼變化，清空錯誤訊息
watch(codeArr, (val) => {
  if (val.some((digit) => digit !== undefined && digit !== null)) {
    errorMessage.value = '';
  }
});

// 驗證碼驗證
const validateCode = async () => {
  const code = codeArr.value.map(String).join('');
  if (code.length !== 6) return;

  try {
    isLoading.value = true;
    errorMessage.value = '';
    await validateCodeApi(code, props.email);
    emit('toStep3', code);
  } catch (error) {
    const apiError = error as ApiErrorWithStatus | Error;
    if ('message' in apiError && apiError.message) {
      errorMessage.value = apiError.message;
    } else {
      errorMessage.value = t('pages.signup.step2.invalidCode');
    }
    // 清空輸入框
    codeArr.value = [];
  } finally {
    isLoading.value = false;
  }
};

// 倒數計時
const countDown = ref(0);

// 重新發送驗證碼
const resendCode = () => {
  sendRegisterCodeApi(props.email);
  // count down
  countDown.value = 30;
  const timer = setInterval(() => {
    countDown.value--;
  }, 1000);

  setTimeout(() => {
    clearInterval(timer);
  }, 30000);
};
</script>